import ErrorIcon from '@mui/icons-material/Error'
import React from 'react'

import { EmptyState } from 'components/empty-state/EmptyState'
import { chartValidationService } from 'features/charts-v2/services/chart-validation-service'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  ChartStrategy,
  ChartValidationResult,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { getMetricsLabel } from 'features/charts-v2/utils/metric-format-utils'

const COMMON_CHART_MARGIN = {
  top: 20,
  right: 30,
  left: 20,
  bottom: 20,
} as const

export abstract class BaseChartStrategy implements ChartStrategy {
  public abstract readonly chartType: ChartTypeV2
  public abstract readonly requiredMetrics: MetricType[]
  public abstract readonly optionalMetrics?: MetricType[]

  public validate(props: ChartProps): ChartValidationResult {
    return chartValidationService.validateChart(this.chartType, props)
  }

  public abstract transform(props: ChartProps): TransformedChartData
  public abstract render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement
  public abstract getTitle(): string

  protected getMetricLabel(selectedMetrics: MetricType[]): string {
    return getMetricsLabel(selectedMetrics)
  }

  protected getCommonMargin(): {
    top: number
    right: number
    left: number
    bottom: number
  } {
    return COMMON_CHART_MARGIN
  }

  protected renderEmptyState(
    title: string,
    description?: string,
  ): React.ReactElement {
    return (
      <EmptyState
        icon={<ErrorIcon style={{ fontSize: '32px' }} />}
        title={title}
        description={description}
        fullHeight="container"
        textWidth="300px"
      />
    )
  }

  protected renderValidationError(
    validation: ChartValidationResult,
  ): React.ReactElement {
    return (
      <EmptyState
        icon={<ErrorIcon style={{ fontSize: '32px' }} />}
        title={validation.errorTitle || 'Validation error'}
        description={validation.errorDescription}
        fullHeight="container"
        textWidth="300px"
      />
    )
  }
}
